import { BodyText, Caption, FieldsetCard } from '@if/mui'
import { Checkbox, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import NsfStatusSelect from '@src/components/LoanCalendar/LoanCalendarPaymentStatus/NsfStatusSelect'
import { EBankFailureCode } from '@src/types/constants'
import type { TestLoanPreviewResult } from '@src/types/test-loan'
import { Control, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TestLoanDraftType } from './test-loan-schema'

interface Props {
  control: Control<TestLoanDraftType>
  preview: TestLoanPreviewResult | null
}

// Lists the payments dated before today; clicking a row marks it as bounced with a failure code.
const PastPaymentsStep = ({ control, preview }: Props) => {
  const { t, i18n } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'failedPayments' })
  const money = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'CAD' })

  if (!preview) return null

  const failedIndex = (date: string) => fields.findIndex((f) => f.effectiveDate?.toJSON() === date)

  const toggle = (date: string) => {
    const index = failedIndex(date)
    if (index >= 0) {
      remove(index)
      return
    }
    const payment = preview.pastPayments.find((p) => p.effectiveDate.toJSON() === date)
    if (payment)
      append({ effectiveDate: payment.effectiveDate, bankFailureCode: EBankFailureCode.NSFInsufficientFunds })
  }

  return (
    <FieldsetCard title={t('loan.createWizard.stepPayments')}>
      <Stack spacing={2}>
        <BodyText>
          {t('loan.createWizard.paymentAmount')}: <strong>{money.format(preview.paymentAmount)}</strong>
        </BodyText>
        {preview.pastPayments.length === 0 ? (
          <Caption>{t('loan.createWizard.noPastPayment')}</Caption>
        ) : (
          <>
            <Caption>{t('loan.createWizard.clickToFail')}</Caption>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">{t('loan.createWizard.failed')}</TableCell>
                  <TableCell>{t('loan.transactions.date')}</TableCell>
                  <TableCell align="right">{t('loan.transactions.amount')}</TableCell>
                  <TableCell>{t('loan.createWizard.failureCode')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.pastPayments.map((p) => {
                  const key = p.effectiveDate.toJSON()
                  const index = failedIndex(key)
                  return (
                    <TableRow
                      key={key}
                      hover
                      selected={index >= 0}
                      onClick={() => toggle(key)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={index >= 0} tabIndex={-1} />
                      </TableCell>
                      <TableCell>{key}</TableCell>
                      <TableCell align="right">{money.format(p.amount)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ minWidth: 260 }}>
                        {index >= 0 && (
                          <NsfStatusSelect control={control} name={`failedPayments.${index}.bankFailureCode`} />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
    </FieldsetCard>
  )
}

export default PastPaymentsStep
